/**
 * Part timeline drawer (PARTS-CUSTODY.md §V2 item 3a): the part's event
 * ledger rendered as a visual chain with names + times — Ordered (PO) →
 * Received → Staged → With tech → Installed / Returned → Checked in.
 * One glance = the part's biography. Shared by the custody page and the
 * job-page parts card so the biography renders identically everywhere.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { X, History, AlertTriangle, Boxes, Truck, Warehouse, Hand, Wrench, Undo2, ClipboardCheck } from "lucide-react";

export interface TimelinePart {
    id: string;
    short_code: string;
    description: string;
    unit_cost: number;
    custody_state: string;
    staged_note: string | null;
    po_number: string | null;
}

interface CustodyEvent {
    id: string;
    from_state: string | null;
    to_state: string;
    note: string | null;
    at: string;
    by_name: string | null;
}

interface PartPoInfo {
    po_number: string | null;
    ordered_at: string | null;
    confirmed_delivery_date: string | null;
    po_created_at: string;
    vendor_name: string | null;
}

export const PART_STATE_LABEL: Record<string, string> = {
    received: "At office",
    staged: "Staged",
    issued: "With tech",
    installed: "Installed",
    returning: "Returning",
    returning_to_vendor: "Returning to vendor",
    checked_in: "Checked in",
    lost: "Lost",
    voided: "Never came",
};

// §V2 contrast doctrine: solid chip backgrounds with white/near-black text —
// never a translucent tint whose contrast depends on what's behind it.
export const PART_STATE_CHIP: Record<string, string> = {
    received: "bg-sky-700 text-white",
    staged: "bg-violet-600 text-white",
    issued: "bg-indigo-600 text-white",
    installed: "bg-emerald-700 text-white",
    returning: "bg-red-600 text-white",
    returning_to_vendor: "bg-red-600 text-white",
    checked_in: "bg-emerald-700 text-white",
    lost: "bg-muted text-muted-foreground line-through",
    voided: "bg-muted text-muted-foreground line-through",
};

export function PartStatePill({ state, stagedNote }: { state: string; stagedNote?: string | null }) {
    return (
        <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${PART_STATE_CHIP[state] ?? "bg-muted text-foreground"}`}
            title={
                state === "staged"
                    ? stagedNote
                        ? `Staged: ${stagedNote}`
                        : "Staged with no location recorded — the tech will have to ask"
                    : undefined
            }
        >
            {PART_STATE_LABEL[state] ?? state}
            {/* #830: a blank location is stated, never silent. */}
            {state === "staged" ? ` · ${stagedNote || "no location"}` : ""}
        </span>
    );
}

const EVENT_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
    received: Warehouse,
    staged: Boxes,
    issued: Hand,
    installed: Wrench,
    returning: Undo2,
    returning_to_vendor: Undo2,
    checked_in: ClipboardCheck,
    lost: AlertTriangle,
    voided: Undo2,
};

const EVENT_DOT: Record<string, string> = {
    received: "bg-sky-600",
    staged: "bg-violet-600",
    issued: "bg-indigo-600",
    installed: "bg-emerald-600",
    returning: "bg-red-600",
    returning_to_vendor: "bg-red-600",
    checked_in: "bg-emerald-700",
    lost: "bg-red-800",
    voided: "bg-muted-foreground",
};

// #829 M4: the custody history used to print the raw machine string
// "auto_threshold" — translate it (and composites containing it) to words.
export function humanizeCustodyNote(note: string | null | undefined): string | null {
    if (!note) return null;
    return note.replace(/\bauto_threshold\b/g, "auto-tracked — cost met the tracking threshold");
}

function TimelineNode({
    icon: Icon,
    dotClass,
    title,
    meta,
    note,
    last,
}: {
    icon: React.ComponentType<{ className?: string }>;
    dotClass: string;
    title: string;
    meta: string;
    note?: string | null;
    last: boolean;
}) {
    return (
        <li className="relative flex gap-3 pb-5 last:pb-0">
            {!last && <span className="absolute left-[13px] top-7 h-[calc(100%-16px)] w-0.5 bg-border" aria-hidden />}
            <span
                className={`z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${dotClass} text-white`}
            >
                <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 pt-0.5">
                <div className="text-sm font-medium">{title}</div>
                <div className="text-xs text-muted-foreground">{meta}</div>
                {note && <div className="mt-0.5 text-xs">{humanizeCustodyNote(note)}</div>}
            </div>
        </li>
    );
}

export function PartTimelineDrawer({ part, onClose }: { part: TimelinePart; onClose: () => void }) {
    const { data } = useQuery({
        queryKey: ["tracked-parts", part.id, "events"],
        queryFn: () =>
            api.get<{ events: CustodyEvent[]; po: PartPoInfo | null }>(`/api/tracked-parts/${part.id}/events`),
    });

    const events = data?.events ?? [];
    const po = data?.po ?? null;
    // Notes (from === to) fold into the preceding transition, so the chain
    // stays one-node-per-state with the note text attached where it happened.
    const transitions = events.filter((e) => e.from_state !== e.to_state || e.from_state === null);
    const notesAfter = (idx: number): CustodyEvent[] => {
        const start = events.indexOf(transitions[idx]);
        const end = idx + 1 < transitions.length ? events.indexOf(transitions[idx + 1]) : events.length;
        return events.slice(start + 1, end).filter((e) => e.from_state === e.to_state);
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
            <div
                className="h-full w-full max-w-md overflow-y-auto border-l bg-card p-4 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-1 flex items-center justify-between">
                    <h2 className="font-semibold">
                        <span className="font-mono">{part.short_code}</span> — {part.description}
                    </h2>
                    <button className="rounded-md border p-1 hover:bg-muted" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <PartStatePill state={part.custody_state} stagedNote={part.staged_note} />
                    <span>${Math.round(part.unit_cost)}</span>
                    {part.po_number && <span>· {part.po_number}</span>}
                </div>
                <ol>
                    {po && (
                        <TimelineNode
                            icon={Truck}
                            dotClass="bg-slate-600"
                            title={`Ordered${po.po_number ? ` (${po.po_number})` : ""}${po.vendor_name ? ` — ${po.vendor_name}` : ""}`}
                            meta={new Date(po.ordered_at ?? po.po_created_at).toLocaleString()}
                            note={
                                po.confirmed_delivery_date
                                    ? `Confirmed for ${new Date(po.confirmed_delivery_date).toLocaleDateString()}`
                                    : null
                            }
                            last={transitions.length === 0}
                        />
                    )}
                    {transitions.map((e, idx) => {
                        const Icon = EVENT_ICON[e.to_state] ?? History;
                        const attached = notesAfter(idx);
                        const noteText = [e.note, ...attached.map((n) => n.note)].filter(Boolean).join(" · ");
                        return (
                            <TimelineNode
                                key={e.id}
                                icon={Icon}
                                dotClass={EVENT_DOT[e.to_state] ?? "bg-slate-600"}
                                title={PART_STATE_LABEL[e.to_state] ?? e.to_state}
                                meta={`${new Date(e.at).toLocaleString()}${e.by_name ? ` · ${e.by_name}` : ""}`}
                                note={noteText || null}
                                last={idx === transitions.length - 1}
                            />
                        );
                    })}
                    {data && transitions.length === 0 && !po && (
                        <li className="text-sm text-muted-foreground">No events.</li>
                    )}
                </ol>
            </div>
        </div>
    );
}
